import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { GrupoMaquina, VisaoGeralMes, VisaoGeralPayload } from '@/types/dashboards';
import {
  divergenciasDeCodigo,
  formatosDaLinha,
  kpisDoMes,
  matrizDesvio,
  maquinasDoMes,
  mesDoPayload,
  serieDiariaPorFormato,
  serieDoMes,
  tabelaSku,
  temPlano,
} from '@/utils/equiplexVisaoGeral';
import { fechadoAte } from '@/utils/equiplexSop';
import { formatosVigentes, mesVigente } from '@/utils/filtroPaineis';
import { imprimirRelatorio } from '@/utils/imprimir';
import { periodoCurto } from '@/utils/painelFormat';
import ApontamentosDoMes from '../apontamentos/ApontamentosDoMes';
import RotuloMes from '../RotuloMes';
import type { CoresPainel } from '../chartTheme';
import AvisosVisaoGeral from './AvisosVisaoGeral';
import DesvioMaquinaMatriz from './DesvioMaquinaMatriz';
import KpisVisaoGeral from './KpisVisaoGeral';
import MaquinasChart from './MaquinasChart';
import ProducaoDiariaFormatoChart from './ProducaoDiariaFormatoChart';
import SkuTable from './SkuTable';
import SopRealizadoChart from './SopRealizadoChart';

interface Props {
  payload: VisaoGeralPayload;
  cores: CoresPainel;
}

const SeletorMes: FC<{ payload: VisaoGeralPayload; mes: string; onMes: (m: string) => void }> = ({ payload, mes, onMes }) => (
  <div role="group" aria-label="Mês de produção" className="flex flex-wrap items-center gap-1.5">
    <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Mês</span>
    {payload.meses.map((m) => {
      const ativo = m.key === mes;
      return (
        <button
          key={m.key}
          type="button"
          aria-pressed={ativo}
          title={`${m.label}: ${m.range}`}
          onClick={() => onMes(m.key)}
          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
            ativo
              ? 'border-accent bg-accent font-semibold text-accent-contrast'
              : 'border-theme-border text-content-secondary hover:text-content-primary'
          }`}
        >
          <RotuloMes label={m.label} periodo={periodoCurto(m)} />
          {m.em_andamento && <span className="font-normal">· parcial</span>}
          {!temPlano(payload, m.key) && <span className="font-normal">· sem S&OP</span>}
        </button>
      );
    })}
  </div>
);

const SeletorGrupo: FC<{
  grupos: VisaoGeralPayload['maquinas']['grupos'];
  grupo: GrupoMaquina;
  onGrupo: (g: GrupoMaquina) => void;
}> = ({ grupos, grupo, onGrupo }) => (
  <div role="group" aria-label="Linha de produção" className="inline-flex rounded-lg border border-theme-border p-0.5 text-xs">
    {grupos.map((g) => (
      <button
        key={g.id}
        type="button"
        aria-pressed={grupo === g.id}
        onClick={() => onGrupo(g.id)}
        className={`rounded-md px-3 py-1.5 ${
          grupo === g.id ? 'bg-surface-hover font-medium text-content-primary' : 'text-content-muted hover:text-content-primary'
        }`}
      >
        {g.rotulo}
      </button>
    ))}
  </div>
);

/** Linha do topo do PDF: o mês (do tipo escolhido no filtro) e, se houver, os produtos. */
const TituloImpressao: FC<{ payload: VisaoGeralPayload; mes: VisaoGeralMes }> = ({ payload, mes }) => {
  const tipo = payload.recorte?.calendario === 'civil' ? 'mês' : 'mês de produção';
  const produtos = (payload.recorte?.produtos ?? []).map((c) => `${c} ${payload.itens[c]?.descricao ?? ''}`.trim());
  return (
    <p className="hidden text-sm font-semibold text-content-primary print:block" data-testid="vg-titulo-impressao">
      Relatório da reunião · {tipo} {mes.label} ({mes.range})
      {produtos.length > 0 && <> · produtos: {produtos.join(', ')}</>}
    </p>
  );
};

/**
 * A Visão geral: o "Daily Tracker" do Manus refeito com dado real.
 *
 * Primeiro o que a reunião olha todo dia — a produção diária por formato contra a meta S&OP
 * do dia, aberta de cara, frascos e ampolas separados (a escala não conversa) —; depois o
 * acumulado do mês contra o plano; embaixo, onde o plano vira execução — por máquina. Período e
 * produto vêm do filtro dos painéis (servidor); o mês dentro do período e os formatos são estado
 * local, que sobrevive a um filtro novo enquanto ainda existir nele. Remonta (via `key`) quando
 * chega snapshot novo.
 */
const PainelVisaoGeral: FC<Props> = ({ payload, cores }) => {
  const [mesEscolhido, setMes] = useState<string | null>(null);
  const mes = mesVigente(mesEscolhido, payload.meses, payload.mes_padrao);
  const [escolhidas, setEscolhidas] = useState<string[] | null>(null);
  const categorias = useMemo(
    () => formatosVigentes(escolhidas, payload.categorias, () => payload.categorias),
    [escolhidas, payload],
  );
  // Formatos desligados nos gráficos diários (todos abrem ligados: o que não aparece, ninguém vê).
  const [ocultos, setOcultos] = useState<string[]>([]);
  const [grupo, setGrupo] = useState<GrupoMaquina>('acabado');

  const mesDesc = mesDoPayload(payload, mes) ?? payload.meses[payload.meses.length - 1];
  const serie = useMemo(() => serieDoMes(payload, mes, categorias), [payload, mes, categorias]);
  const kpis = useMemo(() => kpisDoMes(payload, mes, categorias), [payload, mes, categorias]);
  const skus = useMemo(() => tabelaSku(payload, mes, categorias), [payload, mes, categorias]);
  const divergencias = useMemo(() => divergenciasDeCodigo(skus), [skus]);
  const foraDoPlano = useMemo(() => {
    const linhas = skus.flatMap((g) => g.linhas).filter((l) => l.situacao === 'fora_do_plano');
    return { skus: linhas.length, unidades: linhas.reduce((s, l) => s + l.realizado, 0) };
  }, [skus]);
  const maquinas = useMemo(() => maquinasDoMes(payload, grupo, mes), [payload, grupo, mes]);
  const matriz = useMemo(() => matrizDesvio(payload, grupo), [payload, grupo]);

  const mesTemPlano = temPlano(payload, mes);
  const formatosGrande = useMemo(() => formatosDaLinha(payload, mes, 'grande'), [payload, mes]);
  const formatosPequeno = useMemo(() => formatosDaLinha(payload, mes, 'pequeno'), [payload, mes]);
  const diarioGrande = useMemo(() => serieDiariaPorFormato(payload, mes, formatosGrande), [payload, mes, formatosGrande]);
  const diarioPequeno = useMemo(() => serieDiariaPorFormato(payload, mes, formatosPequeno), [payload, mes, formatosPequeno]);
  const alternarOculto = (f: string) => setOcultos((atual) => (atual.includes(f) ? atual.filter((x) => x !== f) : [...atual, f]));

  const alternarCategoria = (c: string) =>
    setEscolhidas(categorias.includes(c) ? categorias.filter((x) => x !== c) : [...categorias, c]);

  return (
    <>
      <div className="print-hide flex flex-wrap items-center justify-between gap-3">
        {payload.meses.length > 1 ? <SeletorMes payload={payload} mes={mes} onMes={setMes} /> : <span />}
        <button
          type="button"
          onClick={imprimirRelatorio}
          data-testid="vg-pdf"
          title='Abre a impressão (escolha "Salvar como PDF"): a Visão geral do mês escolhido e os apontamentos dele.'
          className="inline-flex items-center gap-2 rounded-lg border border-theme-border px-3 py-1.5 text-sm text-content-secondary hover:bg-surface-hover hover:text-content-primary"
        >
          <span aria-hidden className="material-symbols-outlined text-[18px] leading-none">picture_as_pdf</span>
          Relatório da reunião (PDF)
        </button>
      </div>
      <TituloImpressao payload={payload} mes={mesDesc} />
      <AvisosVisaoGeral
        frescor={payload.frescor}
        plano={payload.plano}
        mes={mesDesc}
        mesTemPlano={mesTemPlano}
        corte={payload.corte}
        fechadoAte={fechadoAte(payload)}
        divergencias={divergencias}
        foraDoFiltro={payload.plano_fora_do_filtro ?? []}
      />
      {kpis && <KpisVisaoGeral kpis={kpis} corte={payload.corte} foraDoPlano={foraDoPlano} />}

      <div className="grid grid-cols-1 gap-4">
        <ProducaoDiariaFormatoChart
          testId="vg-diario-grande"
          titulo="Produção diária — frascos (grande volume)"
          serie={diarioGrande}
          formatos={formatosGrande}
          todas={payload.categorias}
          ocultos={ocultos}
          onAlternar={alternarOculto}
          temPlano={mesTemPlano}
          mes={mesDesc}
          corte={payload.corte}
          cores={cores}
        />
        <ProducaoDiariaFormatoChart
          testId="vg-diario-pequeno"
          titulo="Produção diária — ampolas (pequeno volume)"
          serie={diarioPequeno}
          formatos={formatosPequeno}
          todas={payload.categorias}
          ocultos={ocultos}
          onAlternar={alternarOculto}
          temPlano={mesTemPlano}
          mes={mesDesc}
          corte={payload.corte}
          cores={cores}
        />
      </div>

      <SopRealizadoChart
        serie={serie}
        categorias={payload.categorias}
        selecionadas={categorias}
        onAlternar={alternarCategoria}
        mes={mesDesc}
        corte={payload.corte}
        cores={cores}
      />

      <section className="flex flex-col gap-4" aria-labelledby="vg-por-maquina">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="vg-por-maquina" className="text-lg font-semibold text-content-primary">
              Por máquina
            </h2>
            <p className="text-sm text-content-secondary">
              Onde o plano vira ordem: programado nas OPs × o que foi reportado nelas.{' '}
              <Link
                to="/paineis/equiplex/maquinas?aba=mfr"
                className="print-hide font-medium text-accent hover:underline"
                data-testid="vg-link-maquinas"
              >
                MFR de cada máquina, dia a dia →
              </Link>
            </p>
          </div>
          <SeletorGrupo grupos={payload.maquinas.grupos} grupo={grupo} onGrupo={setGrupo} />
        </div>
        {/* Um embaixo do outro (26/10/2026): o gráfico em cima, a matriz embaixo — com os 6 meses até o fim do período. */}
        <div className="grid grid-cols-1 gap-4">
          <div className="min-w-0">
            <MaquinasChart linhas={maquinas} mes={mesDesc} fechadoAte={fechadoAte(payload)} cores={cores} />
          </div>
          <div className="min-w-0">
            <DesvioMaquinaMatriz
              matriz={matriz}
              meses={payload.meses_historico ?? payload.meses}
              mesSelecionado={mes}
              limiares={payload.limiares}
              modo={cores.modo}
              fechadoAte={fechadoAte(payload)}
            />
          </div>
        </div>
      </section>

      {/* Logo depois das máquinas: é o porquê dos desvios que a matriz mostra. */}
      <ApontamentosDoMes mes={mesDesc} />

      <SkuTable grupos={skus} mesLabel={mesDesc.label} />

      <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="vg-notas">
        <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
          {payload.notas.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>Fonte: réplica MySQL do Datasul (carga diária às 02:00) e Balanceamento de Linha; painel gerado às 06:00, com os dias até 23:59 do dia anterior.</li>
        </ul>
      </details>
    </>
  );
};

export default PainelVisaoGeral;
